import React, { useState, useEffect } from 'react';
import { Search, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Kid } from '../types';
import { getUsers } from '../lib/queries';

interface ExtendedKid extends Kid {
  parentName: string;
  parentEmail: string;
}

const Kids = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKid, setSelectedKid] = useState<ExtendedKid | null>(null);
  const [kids, setKids] = useState<ExtendedKid[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKids = async () => {
      try {
        setLoading(true);
        const users = await getUsers();
        const allKids = users.flatMap(user => 
          user.kids.map(kid => ({
            ...kid,
            parentName: `${user.first_name} ${user.last_name}`,
            parentEmail: user.email
          }))
        );
        setKids(allKids);
      } catch (error) {
        console.error('Error fetching kids:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchKids();
  }, []);

  const filteredKids = kids.filter(kid => 
    kid.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kid.parentName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const QRCodeModal = ({ kid, onClose }: { kid: ExtendedKid; onClose: () => void }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-semibold">Card QR Code</h2>
          <p className="text-gray-600 mt-1">{kid.name}</p>
        </div>
        <div className="p-6 flex flex-col items-center">
          <div className="bg-white p-4 rounded-lg shadow-inner">
            <QRCodeSVG 
              value={kid.code?.uuid || ''}
              size={200}
              level="H"
              includeMargin={true}
            />
          </div>
          <div className="mt-4 text-center space-y-2">
            <div>
              <p className="text-sm text-gray-500">Card UUID</p>
              <p className="font-mono text-sm text-gray-600">{kid.code?.uuid}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Vending Machine Card ID</p>
              <p className="font-mono text-sm text-gray-600">{kid.code?.id_vending_card || 'Not assigned'}</p>
            </div>
          </div>
        </div>
        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative flex-grow max-w-md">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search kids..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Birth Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredKids.map((kid) => (
              <tr key={kid.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-800 font-medium">
                          {kid.name[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{kid.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{kid.parentName}</div>
                  <div className="text-sm text-gray-500">{kid.parentEmail}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(kid.birth_date).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 capitalize">{kid.gender}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">School {kid.school_id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => setSelectedKid(kid)}
                    className="inline-flex items-center px-3 py-1 border border-primary-300 text-sm leading-5 font-medium rounded-full text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:border-primary-400 focus:shadow-outline-primary active:bg-primary-200 transition ease-in-out duration-150"
                  >
                    <QrCode className="h-4 w-4 mr-1" />
                    View Card
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedKid && (
        <QRCodeModal 
          kid={selectedKid} 
          onClose={() => setSelectedKid(null)} 
        />
      )}
    </div>
  );
};

export default Kids;